import React, { memo, useCallback } from 'react';
import { Task, TaskStatus } from '../../types';
import { TaskCard } from '../../components/task';
import { useProject } from '../../context/ProjectContext';
import { useToast } from '../../hooks/useToast';
import { db } from '../../services';

// ============================================
// Types
// ============================================

interface KanbanColumnProps {
  title: string;
  status: TaskStatus;
  tasks: Task[];
  onAddTask?: () => void;
}

// ============================================
// Component
// ============================================

const KanbanColumn: React.FC<KanbanColumnProps> = memo(({ title, status, tasks, onAddTask }) => {
  const { tasks: allTasks, setTasks, addTimelineEvent } = useProject();
  const { showError } = useToast();

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId) return;

    const task = allTasks.find(t => t.id === taskId);
    if (task && task.status !== status) {
      const previousStatus = task.status;
      
      // Optimistic update FIRST
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, status } : t
      ));
      
      try {
        await db.updateTask(taskId, { status });
        // Timeline event (non-blocking)
        addTimelineEvent(task.projectId, 'task_status_changed', `Task "${task.title}" moved to ${title}.`)
          .catch(e => console.warn('Timeline event failed:', e));
      } catch (err) {
        console.error(err);
        // Rollback on error
        setTasks(prev => prev.map(t => 
          t.id === taskId ? { ...t, status: previousStatus } : t
        ));
        showError('Failed to update task status');
      }
    }
  }, [allTasks, status, title, setTasks, addTimelineEvent, showError]);

  const allowDrop = useCallback((e: React.DragEvent) => e.preventDefault(), []);

  const getStatusColors = () => {
    switch(status) {
      case TaskStatus.TODO: return 'border-slate-200 bg-slate-50/50';
      case TaskStatus.IN_PROGRESS: return 'border-indigo-100 bg-indigo-50/30';
      case TaskStatus.DONE: return 'border-emerald-100 bg-emerald-50/30';
      default: return 'border-slate-200 bg-slate-50';
    }
  };

  return (
    <div 
      onDrop={handleDrop} 
      onDragOver={allowDrop}
      className={`flex flex-col w-full min-w-0 rounded-[2rem] p-6 border transition-all duration-300 shadow-sm min-h-[300px] ${getStatusColors()}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 px-1 shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${
            status === TaskStatus.TODO ? 'bg-slate-400' : 
            status === TaskStatus.IN_PROGRESS ? 'bg-indigo-500 animate-pulse' : 'bg-emerald-500'
          }`} />
          <h3 className="font-extrabold text-slate-800 uppercase tracking-widest text-[11px] leading-none">{title}</h3>
          <span className="bg-white/80 backdrop-blur-sm text-slate-600 text-[10px] font-black px-2.5 py-1 rounded-lg border border-slate-100 shadow-xs">
            {tasks.length}
          </span>
        </div>
        {onAddTask && (
          <button 
            onClick={onAddTask}
            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"
            title="Add Task"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
          </button>
        )}
      </div>

      {/* Tasks */}
      <div className="flex-1 space-y-4">
        {tasks.map(task => (
          <TaskCard key={task.id} task={task} />
        ))}
        
        {tasks.length === 0 && (
          <div className="h-24 border-2 border-dashed border-slate-200/50 rounded-3xl flex flex-col items-center justify-center text-slate-400 p-6 text-center transition-all hover:bg-white/40 group">
            <svg className="w-6 h-6 mb-2 opacity-20 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2"/>
            </svg>
            <p className="text-[9px] font-black uppercase tracking-widest opacity-30">Drop or Add Tasks</p>
          </div>
        )}
        
        {onAddTask && (
          <button 
            onClick={onAddTask}
            className="w-full py-4 border-2 border-dashed border-slate-200/80 rounded-3xl text-[9px] font-black uppercase tracking-widest text-slate-400 hover:border-indigo-300 hover:text-indigo-600 hover:bg-white transition-all group"
          >
            + Add New Workflow Item
          </button>
        )}
      </div>
    </div>
  );
});

KanbanColumn.displayName = 'KanbanColumn';

export default KanbanColumn;
